import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { saveDocument, deleteDocument, getDocumentById } from '../services/api.service';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { FaChevronLeft, FaExternalLinkAlt } from 'react-icons/fa';

const flags = {
    fr: '/assets/images/fr.png',
    en: '/assets/images/en.png',
    de: '/assets/images/de.png',
};

const normalizeI18n = (obj) => ({ fr: '', en: '', de: '', ...obj });

const defaultDocument = {
    name: { fr: '', en: '', de: '' },
    description: { fr: '', en: '', de: '' },
    link: { fr: '', en: '', de: '' },
    img: '',
    type: '',
    mandatory: false,
};

const preferredName = (doc) =>
    doc?.name?.fr?.trim() ||
    doc?.name?.en?.trim() ||
    doc?.name?.de?.trim() ||
    '(Sans titre)';

const isValidUrl = (s) => {
    try {
        if (!s) return false;
        new URL(s);
        return true;
    } catch {
        return false;
    }
};

const DocumentEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id && id !== 'new');
    const [document, setDocument] = useState(() => (isEdit ? null : { ...defaultDocument }));
    const [lang, setLang] = useState('fr'); // active language in UI
    // const [imgOk, setImgOk] = useState(true);
    const confirm = useConfirm();

    useEffect(() => {
        if (isEdit) {
            getDocumentById(id)
                .then((doc) =>
                    setDocument({
                        ...doc,
                        name: normalizeI18n(doc.name),
                        description: normalizeI18n(doc.description),
                        link: normalizeI18n(doc.link),
                    })
                )
                .catch(() => {
                    Helper.toast("Impossible de charger le document", "error");
                    navigate('/documents');
                });
        }
    }, [id, isEdit, navigate]);

    const handleNestedChange = (field, lang, value) => {
        setDocument((prev) => ({
            ...prev,
            [field]: { ...prev[field], [lang]: value },
        }));
    };

    // const handleChange = (field, value) => {
    //     setDocument((prev) => ({
    //         ...prev,
    //         [field]: value,
    //     }));
    // };

    const canSave = useMemo(() => {
        // Minimal: need at least a French name (adapt if you prefer any language)
        return Boolean(document?.name?.fr?.trim());
    }, [document]);

    const handleSave = async () => {
        if (!canSave) {
            Helper.toast("Veuillez renseigner au minimum le nom (FR).", "warning");
            return;
        }
        try {
            await saveDocument(document);
            navigate('/documents');
        } catch {
            Helper.toast("Erreur lors de l'enregistrement", "error");
        }
    };

    const handleDelete = async () => {
        if (!document?._id) return;

        const ok = await confirm({
            title: "Attention !",
            message: <>Supprimer ce document <strong>{preferredName(document)}</strong> ?</>,
            confirmText: "Oui",
            cancelText: "Non",
            tone: "warning",
        });

        if (!ok) return Helper.toast("Action annulée", "info");

        try {
            await deleteDocument(document._id);
            navigate("/documents");
        } catch {
            Helper.toast("Erreur lors de la suppression", "error");
        }
    };

    if (!document) return <div className="p-6 text-center">Chargement...</div>;

    return (
        <div className="max-w-5xl mx-auto p-6">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 mb-6">
                <div className="flex justify-between items-center py-3">
                    <button
                        onClick={() => navigate('/documents')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <FaChevronLeft className="h-4 w-4" />
                        Retour
                    </button>
                    <div className="flex gap-2">
                        {isEdit && (
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                                Supprimer
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={!canSave}
                            className={`px-4 py-2 rounded text-white shadow ${canSave
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-gray-300 cursor-not-allowed'
                                }`}
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">Édition du document</h2>

            {/* Language Toggle */}
            <div className="mb-6">
                <div className="inline-flex rounded-xl border bg-white p-1 shadow-sm">
                    {['fr', 'de', 'en'].map((l) => (
                        <button
                            key={l}
                            onClick={() => setLang(l)}
                            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            <img src={flags[l]} alt={l} className="w-5 h-4 rounded-sm" />
                            {l.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Card: Translated fields */}
            <div className="rounded-2xl border bg-white shadow-sm p-5 mb-6">
                <h3 className="text-lg font-semibold mb-4">Contenu ({lang.toUpperCase()})</h3>

                {/* Name */}
                <div className="mb-4">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full border rounded px-3 py-2 shadow-sm"
                        placeholder="Nom du document"
                        value={document.name[lang]}
                        onChange={(e) => handleNestedChange('name', lang, e.target.value)}
                    />
                    {lang === 'fr' && !document.name.fr?.trim() && (
                        <p className="mt-1 text-xs text-red-600">Le nom (FR) est requis.</p>
                    )}
                </div>

                {/* Description (textarea) */}
                <div className="mb-4">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        rows={5}
                        className="w-full border rounded px-3 py-2 shadow-sm resize-y"
                        placeholder="Décrivez le document, les informations attendues, etc."
                        value={document.description[lang]}
                        onChange={(e) => handleNestedChange('description', lang, e.target.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        Conseil : soyez précis pour aider l’utilisateur à fournir la bonne pièce.
                    </p>
                </div>

                {/* Link (URL) */}
                <div className="mb-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Lien du modèle (facultatif)</label>
                    <div className="flex items-stretch gap-2">
                        <input
                            type="url"
                            className="w-full border rounded px-3 py-2 shadow-sm"
                            placeholder="https://exemple.com/aide"
                            value={document.link[lang]}
                            onChange={(e) => handleNestedChange('link', lang, e.target.value)}
                        />
                        <button
                            type="button"
                            disabled={!isValidUrl(document.link[lang])}
                            onClick={() => window.open(document.link[lang], '_blank', 'noopener,noreferrer')}
                            className={`inline-flex items-center gap-2 rounded px-3 py-2 text-sm ${isValidUrl(document.link[lang])
                                ? 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                                : 'border border-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                            title="Ouvrir le lien"
                        >
                            <FaExternalLinkAlt className="h-3.5 w-3.5" />
                            Ouvrir
                        </button>
                    </div>
                </div>
            </div>

            {/* Card: Meta */}
            {/* <div className="rounded-2xl border bg-white shadow-sm p-5">
                <h3 className="text-lg font-semibold mb-4">Paramètres</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Image (URL)</label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2 shadow-sm"
                            placeholder="https://exemple.com/image.jpg"
                            value={document.img}
                            onChange={(e) => {
                                setImgOk(true);
                                handleChange('img', e.target.value);
                            }}
                        />
                        {document.img && imgOk && (
                            <div className="mt-3">
                                <img
                                    src={document.img}
                                    alt="Aperçu"
                                    className="h-28 w-28 rounded-lg border object-cover"
                                    onError={() => setImgOk(false)}
                                />
                            </div>
                        )}
                        {document.img && !imgOk && (
                            <p className="mt-2 text-xs text-red-600">Impossible de charger l’image.</p>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                        <input
                            list="doc-types"
                            type="text"
                            className="w-full border rounded px-3 py-2 shadow-sm"
                            placeholder="ex. facture, pièce d’identité, RIB, contrat…"
                            value={document.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                        />
                        <datalist id="doc-types">
                            <option value="facture" />
                            <option value="contrat" />
                            <option value="pièce d’identité" />
                            <option value="RIB" />
                            <option value="justificatif de domicile" />
                        </datalist>

                        <div className="mt-4">
                            <label className="mb-1 block text-sm font-medium text-gray-700">Obligatoire</label>
                            <button
                                type="button"
                                onClick={() => handleChange('mandatory', !document.mandatory)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${document.mandatory ? 'bg-emerald-600' : 'bg-gray-300'
                                    }`}
                                aria-pressed={document.mandatory}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${document.mandatory ? 'translate-x-5' : 'translate-x-1'
                                        }`}
                                />
                                <span className="sr-only">Basculer obligatoire</span>
                            </button>
                            <p className="mt-1 text-xs text-gray-500">
                                Si activé, ce document sera requis pour finaliser une demande.
                            </p>
                        </div>
                    </div>
                </div>
            </div> */}

            <div className='mt-6 flex m-auto text-center w-full justify-center'>
                <button
                    onClick={handleSave}
                    disabled={!canSave}
                    className={`px-4 py-2 rounded text-white shadow ${canSave
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-300 cursor-not-allowed'
                        }`}
                >
                    Enregistrer
                </button>
            </div>
        </div>
    );
};

export default DocumentEditorPage;